import {inject, Injectable, signal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {EMPTY, Observable, of} from 'rxjs';
import {catchError, filter, map, switchMap} from 'rxjs/operators';
import {CurrentTenantStore} from '../tenant/current-tenant.store';
import {KabeenAiSettings, TenantService} from '../tenant/tenant.service';
import {isTenantSuspended} from 'src/app/utils/tenant-plan.utils';

// Safe fallback, on purpose, when the settings can't be loaded at start: it keeps every
// Kabeen-AI feature gated off AND lets the route guard resolve — otherwise
// getSettingsChanges() never emits, the guard's isChatEnabled() neither, and /prism
// hangs on an infinite spinner.
const DISABLED_KABEEN_AI_SETTINGS: KabeenAiSettings = {
	chatEnabled: false,
	applicationsEnabled: false,
	teamsEnabled: false,
	featuresEnabled: false,
	defaultProvider: 'mistral'
};

@Injectable({providedIn: 'root'})
export class KabeenAiService {

	private tenantStore = inject(CurrentTenantStore);
	private tenantService = inject(TenantService);

	private readonly _settings = signal<KabeenAiSettings | undefined>(undefined);
	/** The workspace's Kabeen AI settings, undefined until they are loaded. */
	readonly settings = this._settings.asReadonly();
	private readonly settings$ = toObservable(this._settings);
	private started = false;

	/** Loads the settings once — the shell and the Prism guard both ask on a hard refresh. */
	initialize(): void {
		if (this.started) return;
		this.started = true;
		this.tenantStore.whenLoaded()
			// A suspended workspace gets a 403 on its settings: everything stays off (KAB-2399).
			.pipe(switchMap(tenant => isTenantSuspended(tenant)
				? of(DISABLED_KABEEN_AI_SETTINGS)
				: this.tenantService.getKabeenAiSettings(tenant.configuration.id)
					.pipe(catchError(() => of(DISABLED_KABEEN_AI_SETTINGS)))))
			.subscribe(settings => this._settings.set(settings));
	}

	/** The settings once loaded, then each change — for the Prism store and guard. */
	getSettingsChanges(): Observable<KabeenAiSettings> {
		return this.settings$.pipe(filter((settings): settings is KabeenAiSettings => settings != null));
	}

	isChatEnabled(): Observable<boolean> {
		return this.getSettingsChanges().pipe(map(s => s.chatEnabled));
	}

	refreshSettings(): void {
		this.tenantService.getKabeenAiSettings(this.tenantStore.requireTenantId())
			// A failed refresh keeps the settings this session has: it must not switch the features off.
			.pipe(catchError(() => EMPTY))
			.subscribe(settings => this._settings.set(settings));
	}
}
